import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RotateCcw, Trash2 } from 'lucide-react';
import { fetchApi } from '../../config/api';
import { useConfirm } from '../../context/ConfirmDialogContext';
import { Alert, Badge, Card, CardActions, EmptyState, PageBackdrop, PageHeader } from '../../components/UI';
import {
  ACTIVE_BACKGROUND_TASK_STATUSES,
  AdminBackgroundTask,
  BACKGROUND_TASK_STATUS_VARIANT,
  FieldRow,
  TaskDuration,
  formatAdminDateTime,
  formatDurationSeconds,
  stepDurationLabel,
  useNowTicker,
} from './adminShared';

const LIST_PATH = '/admin/backgroundtasks';

export default function AdminBackgroundTaskDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [task, setTask] = useState<AdminBackgroundTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<'restart' | 'delete' | null>(null);
  const [error, setError] = useState('');

  // Wie bei den Nutzern liefert das Admin-API nur die Gesamtliste; der passende Eintrag
  // wird hier herausgesucht.
  const loadTask = async () => {
    const res = await fetchApi('/admin/background-tasks');
    const list: AdminBackgroundTask[] = res?.data ?? res ?? [];
    setTask(list.find((entry) => String(entry.id) === id) ?? null);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        await loadTask();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('admin:loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isRunning = task != null && ACTIVE_BACKGROUND_TASK_STATUSES.includes(task.status);
  const now = useNowTicker(isRunning);

  // Solange die Aufgabe laeuft, die Daten nachladen, damit man Fortschritt und Schritte
  // live sieht statt manuell neu laden zu muessen.
  useEffect(() => {
    if (!isRunning) return;
    const refresh = setInterval(() => {
      loadTask().catch(() => {});
    }, 5000);
    return () => clearInterval(refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, id]);

  const runAction = async (kind: 'restart' | 'delete', action: () => Promise<void>) => {
    setError('');
    setPending(kind);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin:actionError'));
    } finally {
      setPending(null);
    }
  };

  const restartTask = () => {
    if (!task) return;
    runAction('restart', async () => {
      await fetchApi(`/admin/background-tasks/${task.id}/restart`, { method: 'POST' });
      await loadTask();
    });
  };

  const deleteTask = async () => {
    if (!task) return;
    const confirmed = await confirm(t('admin:backgroundTasks.deleteConfirm', { id: task.id }));
    if (!confirmed) return;
    runAction('delete', async () => {
      await fetchApi(`/admin/background-tasks/${task.id}`, { method: 'DELETE' });
      navigate(LIST_PATH);
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="glass flex items-center gap-3 rounded-2xl px-6 py-4 text-dark-200">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-300/40 border-t-primary-300" />
          {t('common:status.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <PageBackdrop />

      <div className="relative z-10 container mx-auto space-y-6 px-4 py-8 sm:px-6 sm:py-10">
        <PageHeader
          title={task ? task.type : t('admin:taskDetail.notFound')}
          subtitle={task ? `#${task.id}` : undefined}
          backLink={{ label: t('admin:tabs.backgroundTasks'), onClick: () => navigate(LIST_PATH) }}
        />

        {error && <Alert type="error" message={error} />}

        {!task ? (
          <Card>
            <EmptyState title={t('admin:taskDetail.notFound')} description={t('admin:taskDetail.notFoundDescription')} />
          </Card>
        ) : (
          <>
            <Card
              footer={
                <CardActions
                  actions={[
                    {
                      label: t('admin:backgroundTasks.restart'),
                      icon: <RotateCcw size={16} />,
                      loading: pending === 'restart',
                      disabled: pending !== null || task.status === 'Running',
                      onClick: restartTask,
                    },
                    {
                      label: t('common:actions.delete'),
                      icon: <Trash2 size={16} />,
                      variant: 'danger',
                      loading: pending === 'delete',
                      disabled: pending !== null,
                      onClick: deleteTask,
                    },
                  ]}
                />
              }
            >
              <div className="space-y-3 text-sm">
                <FieldRow label={t('admin:backgroundTasks.columnStatus')}>
                  <Badge variant={BACKGROUND_TASK_STATUS_VARIANT[task.status]} size="sm">
                    {t(`admin:backgroundTasks.status.${task.status}`)}
                  </Badge>
                </FieldRow>
                <FieldRow label={t('admin:backgroundTasks.columnDuration')}>
                  <TaskDuration task={task} now={now} />
                </FieldRow>
                {task.averageDurationSeconds != null && (
                  <FieldRow label={t('admin:taskDetail.averageDuration')}>
                    {formatDurationSeconds(task.averageDurationSeconds)}
                  </FieldRow>
                )}
                <FieldRow label={t('admin:backgroundTasks.columnUser')}>{task.userEmail || '-'}</FieldRow>
                {task.lockedBy && (
                  <FieldRow label={t('admin:backgroundTasks.columnNode')}>
                    <Badge variant="warning" size="sm">{task.lockedBy}</Badge>
                  </FieldRow>
                )}
                <FieldRow label={t('admin:backgroundTasks.columnCreated')}>{formatAdminDateTime(task.createdAt)}</FieldRow>
                <FieldRow label={t('admin:taskDetail.started')}>{formatAdminDateTime(task.startedAt)}</FieldRow>
                <FieldRow label={t('admin:taskDetail.completed')}>{formatAdminDateTime(task.completedAt)}</FieldRow>
                <FieldRow label={t('admin:backgroundTasks.columnAttempts')}>{task.attemptCount}</FieldRow>
                {task.nextAttemptAt && (
                  <FieldRow label={t('admin:taskDetail.nextAttempt')}>{formatAdminDateTime(task.nextAttemptAt)}</FieldRow>
                )}
              </div>

              {task.errorMessage && (
                <div className="mt-4">
                  <Alert type="error" message={task.errorMessage} />
                </div>
              )}
            </Card>

            <Card>
              <h2 className="mb-4 text-lg font-semibold text-dark-50">{t('admin:taskDetail.steps')}</h2>
              {task.steps.length === 0 ? (
                <p className="text-sm text-dark-400">{t('admin:taskDetail.noSteps')}</p>
              ) : (
                <ol className="space-y-2">
                  {task.steps.map((step) => {
                    const duration = stepDurationLabel(step, now);
                    return (
                      <li key={step.stepIndex} className="flex flex-wrap items-center gap-2 text-sm">
                        <Badge variant={BACKGROUND_TASK_STATUS_VARIANT[step.status]} size="sm">
                          {t(`admin:backgroundTasks.status.${step.status}`)}
                        </Badge>
                        <span className="text-dark-100">{step.name}</span>
                        {duration && <span className="text-dark-500">({duration})</span>}
                        {step.message && <span className="text-dark-400">- {step.message}</span>}
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
